import {
  useEnvironmentSuggestions,
  useListCloudIntegrations,
  useListIntegrations,
} from '@plerionhq/design-system';
import type {
  EnvironmentSuggestionResponse,
  Integration,
} from '@plerionhq/design-system';

export type IntegrationProvider = 'AWS' | 'GCP' | 'Azure' | 'Kubernetes';

export interface IntegrationCommonProps {
  provider: IntegrationProvider;
  name: string;
  tenantId: string;
  createdAt: string;
  updatedAt: string;
  integrationId: string;
  organizationId: string;
  environment?: { dimension?: { value?: string } };
}

export interface AWSAccountIntegration extends IntegrationCommonProps {
  awsAccountId: string;
  roleArn: string;
  externalId: string;
  type: 'AWSAccount';
}
export type ScanType = 'VULNERABILITY' | 'SENSITIVE_DATA' | 'SBOM' | 'MALWARE';

export interface AzureActiveDirectoryIntegration extends IntegrationCommonProps {
  clientId: string;
  clientSecret: string;
  directoryId: string;
  type: 'AzureActiveDirectory';
}

export type UseDefault = {
  useDefault: boolean;
  enabled?: boolean;
};

export type AzureCWPPApplianceRegionConfig = {
  region?: string;
  enabled?: boolean;
  securityGroupId?: string;
  subnetId?: string;
};

export type AzureWorkloadType =
  | 'Azure::VirtualMachine::Instance'
  | 'Azure::VirtualMachine::ScaleSet'
  | 'Azure::FunctionApp::App'
  | 'Azure::FunctionApp::Function'
  | 'Azure::AppServices::WebApp'
  | 'Azure::Container::App'
  | 'Azure::Container::Instance'
  | 'Azure::Batch::Pool'
  | 'Azure::Batch::Account';

export interface CWPPScanSetting {
  enabled: boolean;
  type: ScanType;
}

export interface AzureCWPPWorkloadSetting {
  enabled: boolean;
  type: AzureWorkloadType;
}

export type CWPPKMSAccessMode = 'ALL_KEYS' | 'SELECTED_KEYS';

export interface AzureCWPPConfig {
  applianceRegionConfigs: {
    [region: string]: AzureCWPPApplianceRegionConfig | UseDefault;
  };
  defaultScanSettings: CWPPScanSetting[];
  workloadSettings: AzureCWPPWorkloadSetting[];
}

export interface AzureSubscriptionIntegration extends IntegrationCommonProps {
  parentIntegrationId: string;
  subscriptionId: string;
  type: 'AzureSubscription';
  workloadEnabled?: boolean;
  workloadConfig?: AzureCWPPConfig;
  cwppErrors?: Error[];
}

export interface GCPAccountIntegration extends IntegrationCommonProps {
  projectId: string;
  clientEmail: string;
  privateKey: string;
  type: 'GCPAccount';
}

export type KubernetesCapability = 'KSPM' | 'CWPP';
export interface KubernetesIntegration extends IntegrationCommonProps {
  type: 'Kubernetes';
  kubernetesCapabilities: KubernetesCapability[];
  operatorVersion?: string;
  description?: string;
  integrationKey?: string;
}

export type IntegrationType =
  | AWSAccountIntegration
  | AzureActiveDirectoryIntegration
  | AzureSubscriptionIntegration
  | GCPAccountIntegration
  | KubernetesIntegration;

export const useTenantHasCloudProviderIntegrated = () => {
  const { data } = useListCloudIntegrations();
  if (data === undefined) {
    return undefined;
  }
  return data.length > 0;
};

export const useTenantHasIntegrationsWithEnvironmentSuggestions = () => {
  const { data: integrations, isLoading: isIntegrationsLoading } =
    useListIntegrations();
  const integrationIdsWithoutEnvironment =
    integrations
      ?.filter((i: Integration) => !i.environment?.dimension?.value)
      .map((item: Integration) => item.integrationId) ?? [];
  const {
    data: environmentSuggestions,
    isLoading: isEnvironmentSuggestionsLoading,
  } = useEnvironmentSuggestions(integrationIdsWithoutEnvironment);

  if (isIntegrationsLoading || isEnvironmentSuggestionsLoading) {
    return;
  }

  // Return clean boolean once loaded
  return (
    environmentSuggestions !== undefined &&
    environmentSuggestions.some(
      (item: EnvironmentSuggestionResponse) =>
        item.suggestion?.confidenceLevel === 'high',
    )
  );
};

export function useHasAWSIntegrations() {
  const { data } = useListCloudIntegrations();
  return !!data?.some((i) => i.provider === 'AWS' && i.type === 'AWSAccount');
}
